import { noop } from "lodash";
import {
  type ChangeEvent,
  type FocusEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/ui/form";
import type { InputProps } from "@/components/ui/input";
import { TypographyCaption } from "@/components/ui/typography";
import { IbanInput as IbanInputComponent } from "@/components/ui/iban-input";

type Props = InputProps & {
  name: string;
  label?: string;
  autoFocus?: boolean;
  description?: string;
  selectTextOnFocus?: boolean;
  optional?: boolean;
};

const IbanInputField = ({
  field,
  label,
  description,
  placeholder,
  selectTextOnFocus = false,
  onFocus = noop,
  optional = false,
  ...rest
}: Props & { field: any }) => {
  const { t } = useTranslation();
  const { error } = useFormField();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(field.value);

  useEffect(() => {
    setValue(field.value);
  }, [field.value]);

  useEffect(() => {
    const handleBeforeSubmit = () => {
      if (
        document.activeElement === inputRef.current &&
        value !== field.value
      ) {
        field.onChange(value);
      }
    };

    const forms = inputRef.current?.closest("form");

    if (forms) {
      forms.addEventListener("submit", handleBeforeSubmit);
    }

    return () => {
      if (forms) {
        forms.removeEventListener("submit", handleBeforeSubmit);
      }
    };
  }, [field, value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let newValue: string | null = event.target.value;
    if (newValue === "") {
      newValue = null;
    }
    setValue(newValue);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (value !== field.value) {
      field.onChange(value);
    }
    field.onBlur();
    rest.onBlur?.(event);
  };

  return (
    <FormItem>
      {label && <FormLabel>{label}</FormLabel>}
      {optional && (
        <TypographyCaption className="text-muted-foreground capitalize !mt-0">
          {`(${t("optional")})`}
        </TypographyCaption>
      )}
      <FormControl>
        <IbanInputComponent
          id={`input-${field.name}`}
          ref={inputRef}
          placeholder={placeholder}
          onFocus={(event) => {
            selectTextOnFocus && event.target.select();
            onFocus(field.value);
          }}
          {...field}
          {...rest}
          value={value ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-invalid={error ? true : undefined}
        />
      </FormControl>
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage />
    </FormItem>
  );
};

export const IbanInput = (props: Props) => {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={props.name}
      render={({ field }) => <IbanInputField {...props} field={field} />}
    />
  );
};
